import type { RowSpanMeta } from "@shared/utils/tableRowSpan";
import { t } from "@shared/utils/i18n";
import type { DfCodeMapping, DfOverseasSkuItem, DfSellableProductRow, DfThirdPartyStockDetail, DfWarehouseLineFields, DfZoneBlock } from "./types";

export const DEFAULT_DF_SELLABLE_RATIO = 0.8;
/** 同一 1P 仓下海外仓 SKU 默认展示条数，超出折叠 */
export const DF_ZONE_SKU_VISIBLE_LIMIT = 2;
/** 1P 仓库名称格内默认展示条数（一行一仓时通常仅 1 条；兜底多名称时超出可展开/收起） */
export const DF_WAREHOUSE_NAME_VISIBLE_LIMIT = 2;
/** 一行 = 一个 1P 仓 + 一个仓码(Y)：同产品默认展示行数，超出后插入「展开内容 +N」占位行（对齐产品管理） */
export const DF_WAREHOUSE_ROW_VISIBLE_LIMIT = 3;

export type DfSellableToggleType = "expand" | "collapse";

export type DfSellableFlatRow = DfSellableProductRow &
    RowSpanMeta &
    DfWarehouseLineFields & {
        _isToggleRow?: boolean;
        _toggleType?: DfSellableToggleType;
        _toggleCount?: number;
        /** ASIN/产品维度是否暂停同步（与状态列一致，供仓行 DF 可售编辑判断） */
        _asinPausedSync?: boolean;
    } & Record<string, unknown>;

export function makeDfZoneExpandKey(productId: string, zoneKey: string): string {
    return `${productId}::${zoneKey}`;
}

const ZONE_FALLBACK = [
    { zoneKey: "zone-1", zoneLabel: () => t("分区1") },
    { zoneKey: "zone-2", zoneLabel: () => t("分区2") },
    { zoneKey: "zone-3", zoneLabel: () => t("分区3") },
];

export function calcAmazonSellableByRatio(thirdPartyStock: number, ratio = DEFAULT_DF_SELLABLE_RATIO): number {
    return Math.floor((Number(thirdPartyStock) || 0) * ratio);
}

export function formatDfStockNumber(value: number | undefined | null): string {
    if (value == null || Number.isNaN(Number(value))) return "--";
    return Number(value).toLocaleString("en-US");
}

export function resolveAmazonSellableQtyMax(availableQty: number): number {
    return Math.max(0, Number(availableQty) || 0);
}

export function resolveCurrentSellableQty(product: DfSellableProductRow, _mode: "sum" = "sum"): number {
    return (product.zones || []).reduce((s, z) => s + (Number(z.currentSellableQty) || 0), 0);
}

function emptyWarehouseLine(zoneKey = "zone-1", zoneLabel = t("分区1")): DfWarehouseLineFields {
    return {
        warehouseName: "--",
        warehouseNames: ["--"],
        zoneKey,
        zoneLabel,
        overseasSkus: [],
        overseasSku: "--",
        availableQty: null,
        warningStock: 0,
        preSaleQty: 0,
        dfSellableX: null,
        warehouseCode: "--",
        amazonSellableQty: 0,
        updateTime: "--",
        platformUpdateTimeLabel: "--",
        mappingId: "",
        warehouseId: "",
        paused: false,
        switchDisabled: false,
        apiStatusLabel: "",
        updateFailReason: "",
        dfCodeStatus: "",
        dfCodeTip: "",
        dfCodeSuppressedTips: [],
    };
}

/** warehouseCodes[].apiStatus：0空闲 1待推送 2推送中 3成功 4失败 */
export const DF_API_STATUS = {
    Idle: 0,
    Pending: 1,
    Pushing: 2,
    Success: 3,
    Fail: 4,
} as const;

export type DfApiStatusTone = "updating" | "success" | "error";

export function resolveDfApiStatusTone(status?: number, label = ""): DfApiStatusTone {
    const text = String(label).trim();
    if (text.includes("失败")) return "error";
    if (text.includes("更新中")) return "updating";
    if (text.includes("成功")) return "success";
    const n = Number(status);
    if (n === DF_API_STATUS.Fail) return "error";
    if (n === DF_API_STATUS.Pending || n === DF_API_STATUS.Pushing) return "updating";
    if (n === DF_API_STATUS.Success) return "success";
    return "updating";
}

/** 失败时把 updateFailReason 直接拼在 apiStatusLabel 后面 */
export function buildDfApiStatusDisplay(label?: string, failReason?: string, status?: number): string {
    const text = String(label || "").trim();
    const reason = String(failReason || "").trim();
    const isFail = Number(status) === DF_API_STATUS.Fail || text.includes("失败");
    if (isFail && reason && !text.includes(reason)) return `${text}${reason}`;
    return text;
}

export function buildDfStockDetailsFromMappings(mappings: DfCodeMapping[] | undefined, fallbackSku = "--"): DfThirdPartyStockDetail[] {
    if (!mappings?.length) return [];
    const rows: DfThirdPartyStockDetail[] = [];
    for (const mapping of mappings) {
        const warehouses = mapping.warehouseNames?.filter(Boolean) ?? [];
        if (!warehouses.length) {
            rows.push({
                warehouseName: "--",
                dfCode: mapping.dfCode || "--",
                overseasSku: fallbackSku,
                skuQty: Number(mapping.amazonSellableQty) || 0,
                availableQty: Number(mapping.amazonSellableQty) || 0,
                mappingId: mapping.id,
                updateTime: mapping.updateTime,
                dfSellableX: mapping.amazonSellableQty,
                paused: mapping.paused,
                switchDisabled: mapping.switchDisabled,
                apiStatus: mapping.apiStatus,
                apiStatusLabel: mapping.apiStatusLabel,
                updateFailReason: mapping.updateFailReason,
            });
            continue;
        }
        warehouses.forEach((name, idx) => {
            rows.push({
                warehouseName: name,
                dfCode: mapping.dfCode || "--",
                overseasSku: fallbackSku,
                skuQty: idx === 0 ? Number(mapping.amazonSellableQty) || 0 : 0,
                availableQty: idx === 0 ? Number(mapping.amazonSellableQty) || 0 : 0,
                hideDfCode: idx > 0,
                mappingId: mapping.id,
                updateTime: mapping.updateTime,
                dfSellableX: idx === 0 ? mapping.amazonSellableQty : 0,
                paused: mapping.paused,
                switchDisabled: mapping.switchDisabled,
                apiStatus: mapping.apiStatus,
                apiStatusLabel: mapping.apiStatusLabel,
                updateFailReason: mapping.updateFailReason,
            });
        });
    }
    return rows;
}

function findMappingForDetail(zone: DfZoneBlock, detail: DfThirdPartyStockDetail): DfCodeMapping | undefined {
    if (detail.mappingId) return zone.mappings?.find(m => m.id === detail.mappingId);
    if (detail.dfCode) return zone.mappings?.find(m => m.dfCode === detail.dfCode);
    return zone.mappings?.[0];
}

function toOverseasSkuItem(detail: DfThirdPartyStockDetail): DfOverseasSkuItem {
    return {
        overseasSku: detail.overseasSku || "--",
        qty: Number(detail.skuQty ?? detail.availableQty) || 0,
    };
}

/** 同区 / 同仓码(Y) / 同一 1P 仓的多条 stockDetail（多海外仓 SKU）合并为一行 */
function buildWarehouseLineFromDetails(row: DfSellableProductRow, zone: DfZoneBlock, details: DfThirdPartyStockDetail[]): DfWarehouseLineFields {
    const first = details[0];
    const mapping = findMappingForDetail(zone, first);
    const amazonQty = mapping?.amazonSellableQty ?? first.dfSellableX ?? 0;
    const overseasSkus = details.map(toOverseasSkuItem);
    const warehouseName = String(first.warehouseName || "").trim() || "--";
    const warehouseNames = warehouseName !== "--" ? [warehouseName] : ["--"];

    // 列表「可用库存」← 当前 1P 仓 warehouseCodes[].warehouses[].matchedAvailableQty
    const availableQty = first.matchedAvailableQty != null && Number.isFinite(Number(first.matchedAvailableQty)) ? Number(first.matchedAvailableQty) : Number(first.availableQty) || 0;
    const dfSellableX = Number(first.dfSellableX) || 0;

    // 预警库存 ← warehouseCodes[].warehouses[].warningStock
    const warningRaw =
        first.warningStock != null && Number.isFinite(Number(first.warningStock))
            ? Number(first.warningStock)
            : first.preSaleQty != null && Number.isFinite(Number(first.preSaleQty))
              ? Number(first.preSaleQty)
              : (zone.warningStock ?? zone.preSaleQty ?? row.warningStock);
    const warningStock = warningRaw != null && Number.isFinite(Number(warningRaw)) ? Number(warningRaw) : 0;

    return {
        warehouseName,
        warehouseNames,
        zoneKey: zone.zoneKey,
        zoneLabel: zone.zoneLabel,
        overseasSkus,
        overseasSku: overseasSkus[0]?.overseasSku || "--",
        availableQty,
        warningStock,
        preSaleQty: warningStock,
        dfSellableX,
        warehouseCode: first.dfCode || mapping?.dfCode || "--",
        amazonSellableQty: amazonQty,
        updateTime: first.updateTime || mapping?.updateTime || "--",
        platformUpdateTimeLabel: first.updateTime || mapping?.updateTime || "--",
        mappingId: first.mappingId || mapping?.id || "",
        warehouseId: first.warehouseId || details.find(d => d.warehouseId)?.warehouseId || "",
        mappingRemark: mapping?.remark,
        qtyError: first.qtyError || details.find(d => d.qtyError)?.qtyError,
        paused: first.paused ?? mapping?.paused ?? false,
        switchDisabled: Boolean(row.syncDisabled || first.switchDisabled || mapping?.switchDisabled),
        apiStatus: first.apiStatus ?? mapping?.apiStatus,
        apiStatusLabel: first.apiStatusLabel || mapping?.apiStatusLabel || "",
        updateFailReason: first.updateFailReason || mapping?.updateFailReason || "",
        dfCodeStatus: mapping?.status || "",
        dfCodeTip: mapping?.tip || "",
        dfCodeSuppressedTips: mapping?.suppressedTips || [],
    };
}

function groupDetailsByWarehouse(details: DfThirdPartyStockDetail[]): DfThirdPartyStockDetail[][] {
    const map = new Map<string, DfThirdPartyStockDetail[]>();
    for (const detail of details) {
        const key = String(detail.warehouseName || "--");
        const list = map.get(key);
        if (list) list.push(detail);
        else map.set(key, [detail]);
    }
    return [...map.values()];
}

export function expandDfWarehouseLines(row: DfSellableProductRow): DfWarehouseLineFields[] {
    const lines: DfWarehouseLineFields[] = [];
    const zones: DfZoneBlock[] = row.zones?.length
        ? row.zones
        : ZONE_FALLBACK.map(z => ({
              zoneKey: z.zoneKey,
              zoneLabel: z.zoneLabel(),
              thirdPartyStock: 0,
              currentSellableQty: 0,
              latestUpdateRelative: "--",
              mappings: [] as DfCodeMapping[],
          }));

    for (const zone of zones) {
        // 海外仓 SKU / X 只认 warehouseCodes[].warehouses[]（及 children）；
        // warehouses 为空时不得用根级 sku1p 冒充海外仓 SKU
        const details = zone.stockDetails || [];

        if (!details.length) {
            const mapping = zone.mappings?.[0];
            const names = (mapping?.warehouseNames || []).map(name => String(name || "").trim()).filter(Boolean);
            const warehouseNames = names.length ? names : ["--"];
            // 无 warehouses 明细时仍按 1P 仓拆行（有几个仓名就几行）
            for (const warehouseName of warehouseNames) {
                lines.push({
                    ...emptyWarehouseLine(zone.zoneKey, zone.zoneLabel),
                    warehouseName,
                    warehouseNames: [warehouseName],
                    overseasSkus: [],
                    overseasSku: "--",
                    warehouseCode: mapping?.dfCode || "--",
                    amazonSellableQty: mapping?.amazonSellableQty ?? 0,
                    // 无 warehouses 明细：可用库存 / X / 海外仓 SKU 均按空展示 --
                    dfSellableX: null,
                    availableQty: null,
                    warningStock: zone.warningStock ?? zone.preSaleQty ?? row.warningStock ?? 0,
                    preSaleQty: zone.warningStock ?? zone.preSaleQty ?? row.warningStock ?? 0,
                    updateTime: mapping?.updateTime || "--",
                    platformUpdateTimeLabel: mapping?.updateTime || "--",
                    mappingId: mapping?.id || "",
                    mappingRemark: mapping?.remark,
                    paused: mapping?.paused ?? false,
                    switchDisabled: Boolean(row.syncDisabled || mapping?.switchDisabled),
                    apiStatus: mapping?.apiStatus,
                    apiStatusLabel: mapping?.apiStatusLabel || "",
                    updateFailReason: mapping?.updateFailReason || "",
                    dfCodeStatus: mapping?.status || "",
                    dfCodeTip: mapping?.tip || "",
                    dfCodeSuppressedTips: mapping?.suppressedTips || [],
                });
            }
            continue;
        }

        // 一行 = 一个 1P 仓 + 一个仓码(Y)；同仓多 SKU 合并进 overseasSkus（格内最多展示 2 条）
        for (const group of groupDetailsByWarehouse(details)) {
            lines.push(buildWarehouseLineFromDetails(row, zone, group));
        }
    }

    return lines.length ? lines : [emptyWarehouseLine()];
}

/**
 * 展平为表格行；同产品 1P 仓行数超出 DF_WAREHOUSE_ROW_VISIBLE_LIMIT 时截断，
 * 并插入合并展开/收起占位行（UiTableExpandToggle，对齐产品管理）。
 */
export function flattenDfSellableRows(rows: DfSellableProductRow[], expandedProductIds: ReadonlySet<string | number> = new Set()): DfSellableFlatRow[] {
    const result: DfSellableFlatRow[] = [];

    for (const row of rows) {
        const items = expandDfWarehouseLines(row);
        // 产品/ASIN 维度暂停：仅 syncStatus 暂停（与系统锁禁用区分）
        const anchor = items[0];
        const asinPausedSync = Boolean(anchor?.paused);

        const needToggle = items.length > DF_WAREHOUSE_ROW_VISIBLE_LIMIT;
        const expanded = needToggle && expandedProductIds.has(row.id);
        const visibleItems = needToggle && !expanded ? items.slice(0, DF_WAREHOUSE_ROW_VISIBLE_LIMIT) : items;
        const dataCount = visibleItems.length;
        const toggleCount = needToggle ? items.length - DF_WAREHOUSE_ROW_VISIBLE_LIMIT : 0;

        visibleItems.forEach((item, index) => {
            result.push({
                ...row,
                ...item,
                // 列表预警库存 ← warehouseCodes[].warehouses[].warningStock（展平行已写入 item）
                warningStock: item.warningStock,
                preSaleQty: item.warningStock,
                switchDisabled: Boolean(item.switchDisabled || row.syncDisabled),
                _asinPausedSync: asinPausedSync,
                _isToggleRow: false,
                _rowSpanKey: row.id,
                _rowSpanCount: dataCount,
                _rowSpanIndex: index,
                _rowKey: `${String(row.id)}-${item.zoneKey}-${item.warehouseName}-${index}`,
            } as DfSellableFlatRow);
        });

        if (needToggle) {
            result.push({
                ...row,
                ...emptyWarehouseLine(),
                _asinPausedSync: asinPausedSync,
                _isToggleRow: true,
                _toggleType: expanded ? "collapse" : "expand",
                _toggleCount: toggleCount,
                _rowSpanKey: row.id,
                _rowSpanCount: dataCount,
                _rowSpanIndex: -1,
                _rowKey: `${String(row.id)}-warehouse-toggle`,
            } as DfSellableFlatRow);
        }
    }

    return result;
}

export function mapDfCheckboxRecords(records: unknown[]): DfSellableProductRow[] {
    const map = new Map<string | number, DfSellableProductRow>();
    for (const raw of records as DfSellableFlatRow[]) {
        if (!raw || raw._isToggleRow || raw.id == null || map.has(raw.id)) continue;
        map.set(raw.id, raw);
    }
    return [...map.values()];
}

export function findDfMappingOnProduct(product: DfSellableProductRow, zoneKey: string, mappingId: string): { zone: DfZoneBlock; mapping: DfCodeMapping } | null {
    const zone = product.zones?.find(z => z.zoneKey === zoneKey);
    const mapping = zone?.mappings?.find(m => m.id === mappingId);
    if (!zone || !mapping) return null;
    return { zone, mapping };
}

export function findDfStockDetailOnProduct(
    product: DfSellableProductRow,
    row: Pick<DfWarehouseLineFields, "zoneKey" | "mappingId" | "warehouseName" | "overseasSku">,
): { zone: DfZoneBlock; detail: DfThirdPartyStockDetail } | null {
    const zone = product.zones?.find(z => z.zoneKey === row.zoneKey);
    if (!zone?.stockDetails?.length) return null;
    const detail = zone.stockDetails.find(d => {
        const sameWarehouse = String(d.warehouseName || "--") === String(row.warehouseName || "--");
        const sameSku = String(d.overseasSku || "--") === String(row.overseasSku || "--");
        if (!sameWarehouse || !sameSku) return false;
        if (d.mappingId && row.mappingId) return String(d.mappingId) === String(row.mappingId);
        return true;
    });
    if (!detail) return null;
    return { zone, detail };
}

/** 分区下三方仓库存悬停明细：仅 warehouses 映射出的 stockDetails，无则空 */
export function resolveDfZoneStockDetails(row: Pick<DfSellableProductRow, "zones" | "sku1p">, zoneKey: string): DfThirdPartyStockDetail[] {
    const zone = row.zones?.find(z => z.zoneKey === zoneKey);
    if (!zone?.stockDetails?.length) return [];
    return zone.stockDetails;
}

export function setDfWarehouseLinePaused(product: DfSellableProductRow, row: Pick<DfWarehouseLineFields, "zoneKey" | "mappingId" | "warehouseName" | "overseasSku">, paused: boolean): boolean {
    const zone = product.zones?.find(z => z.zoneKey === row.zoneKey);
    if (!zone) return false;

    if (!zone.stockDetails?.length) {
        zone.stockDetails = buildDfStockDetailsFromMappings(zone.mappings, product.sku1p || "--");
    }

    // 暂停作用于该 1P 仓下全部 SKU 明细；暂停时清空该仓 DF 可售
    const warehouse = String(row.warehouseName || "--");
    let touched = false;
    for (const detail of zone.stockDetails || []) {
        if (String(detail.warehouseName || "--") !== warehouse) continue;
        if (row.mappingId && detail.mappingId && String(detail.mappingId) !== String(row.mappingId)) continue;
        detail.paused = paused;
        if (paused) detail.dfSellableX = 0;
        touched = true;
    }
    if (touched) {
        const foundMapping = findDfMappingOnProduct(product, row.zoneKey, row.mappingId);
        if (foundMapping && paused) {
            foundMapping.mapping.amazonSellableQty = 0;
            foundMapping.mapping.paused = true;
            foundMapping.zone.currentSellableQty = foundMapping.zone.mappings.reduce((s, m) => s + (Number(m.amazonSellableQty) || 0), 0);
            product.currentSellableQty = resolveCurrentSellableQty(product, "sum");
        } else if (foundMapping) {
            foundMapping.mapping.paused = paused;
        }
        return true;
    }

    const foundMapping = findDfMappingOnProduct(product, row.zoneKey, row.mappingId);
    if (foundMapping) {
        foundMapping.mapping.paused = paused;
        if (paused) {
            foundMapping.mapping.amazonSellableQty = 0;
            foundMapping.zone.currentSellableQty = foundMapping.zone.mappings.reduce((s, m) => s + (Number(m.amazonSellableQty) || 0), 0);
            product.currentSellableQty = resolveCurrentSellableQty(product, "sum");
        }
        return true;
    }
    return false;
}

/** 产品级暂停：同步该产品下全部分区明细 / mapping；暂停时清空 DF 可售库存 */
export function setDfProductPaused(product: DfSellableProductRow, paused: boolean): boolean {
    let touched = false;
    for (const zone of product.zones || []) {
        if (!zone.stockDetails?.length) {
            zone.stockDetails = buildDfStockDetailsFromMappings(zone.mappings, product.sku1p || "--");
        }
        for (const detail of zone.stockDetails || []) {
            detail.paused = paused;
            if (paused) detail.dfSellableX = 0;
            touched = true;
        }
        for (const mapping of zone.mappings || []) {
            mapping.paused = paused;
            if (paused) mapping.amazonSellableQty = 0;
            touched = true;
        }
        if (paused) zone.currentSellableQty = 0;
    }
    if (paused) product.currentSellableQty = 0;
    return touched;
}

/** 收集选中产品下全部仓码 id（warehouseCodes[].id，供 batchUpdateStock） */
export function collectDfBatchModifyInventoryIds(products: DfSellableProductRow[]): string[] {
    const ids: string[] = [];
    const seen = new Set<string>();
    for (const product of products) {
        for (const zone of product.zones || []) {
            for (const mapping of zone.mappings || []) {
                const mappingId = String(mapping.id || "").trim();
                if (!mappingId || seen.has(mappingId)) continue;
                seen.add(mappingId);
                ids.push(mappingId);
            }
        }
    }
    return ids;
}

/** @deprecated 本地预计算；批量改库存请用 collectDfBatchModifyInventoryIds + batchUpdateStock */
export function collectDfBatchModifyInventoryUpdates(products: DfSellableProductRow[], options: { mode: "absolute" | "percent"; value: number }): Array<{ id: string; platformStock: number }> {
    const value = Number(options.value);
    if (!Number.isFinite(value) || value < 0) return [];

    const updates: Array<{ id: string; platformStock: number }> = [];
    for (const product of products) {
        for (const zone of product.zones || []) {
            for (const mapping of zone.mappings || []) {
                const mappingId = String(mapping.id || "").trim();
                if (!mappingId) continue;
                const relatedDetails = (zone.stockDetails || []).filter(d => !d.mappingId || String(d.mappingId) === mappingId);
                const availableFromDetails = relatedDetails.reduce((max, d) => Math.max(max, Number(d.availableQty) || 0), 0);
                const available = availableFromDetails || Number(zone.thirdPartyStock) || 0;
                let next = options.mode === "percent" ? Math.round((available * Math.min(value, 100)) / 100) : Math.floor(value);
                if (available > 0) next = Math.min(next, available);
                next = Math.max(0, next);
                updates.push({ id: mappingId, platformStock: next });
            }
        }
    }
    return updates;
}

/** 批量按绝对值 Y / 百分比 X 更新选中产品 DF 可售库存（本地同步展示；提交后建议刷新列表） */
export function applyDfBatchModifyInventory(products: DfSellableProductRow[], options: { mode: "absolute" | "percent"; value: number }): number {
    const updates = collectDfBatchModifyInventoryUpdates(products, options);
    if (!updates.length) return 0;

    const nextById = new Map(updates.map(item => [item.id, item.platformStock]));
    let touched = 0;

    for (const product of products) {
        for (const zone of product.zones || []) {
            for (const mapping of zone.mappings || []) {
                const mappingId = String(mapping.id || "").trim();
                if (!mappingId || !nextById.has(mappingId)) continue;
                const next = nextById.get(mappingId) ?? 0;
                mapping.amazonSellableQty = next;
                const relatedDetails = (zone.stockDetails || []).filter(d => !d.mappingId || String(d.mappingId) === mappingId);
                for (const detail of relatedDetails) {
                    detail.dfSellableX = next;
                }
                touched += 1;
            }
            zone.currentSellableQty = zone.mappings.reduce((s, m) => s + (Number(m.amazonSellableQty) || 0), 0);
        }
        product.currentSellableQty = resolveCurrentSellableQty(product, "sum");
    }
    return touched;
}

export function validateAmazonSellableQty(qty: number, options: { maxThirdPartyStock: number }): { ok: true; value: number } | { ok: false; message: string } {
    const value = Number(qty);
    if (!Number.isFinite(value) || value < 0) {
        return { ok: false, message: t("库存数量无效") };
    }
    if (value > options.maxThirdPartyStock) {
        return { ok: false, message: t("可售库存不能大于可用库存") };
    }
    return { ok: true, value };
}

export function validateAsinZoneDfUniqueness(
    product: Pick<DfSellableProductRow, "asin" | "hasSpecialUpdateRule" | "zones">,
): { ok: true } | { ok: false; message: string; zoneKey: string; dfCodes: string[] } {
    for (const zone of product.zones || []) {
        if (zone.mappings.length > 1 && !product.hasSpecialUpdateRule) {
            return {
                ok: false,
                message: t("一个ASIN不允许设置相同区域的多DF代码，特殊情况，需要设置更新规则"),
                zoneKey: zone.zoneKey,
                dfCodes: zone.mappings.map(m => m.dfCode).filter(Boolean),
            };
        }
    }
    return { ok: true };
}

export function findDfUniquenessViolations(rows: DfSellableProductRow[]): Array<{ asin: string; zoneKey: string; message: string }> {
    const out: Array<{ asin: string; zoneKey: string; message: string }> = [];
    for (const row of rows) {
        const rule = validateAsinZoneDfUniqueness(row);
        if (!rule.ok) {
            out.push({ asin: row.asin, zoneKey: rule.zoneKey, message: rule.message });
        }
    }
    return out;
}
